import React, { useState, useEffect, useMemo } from 'react';
import LanguageSelectionModal from './modals/LanguageSelectionModal';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setDocumentDirection, isRTL as getIsRTL } from '../../../utils/rtlUtils';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupsIcon from '@mui/icons-material/Groups';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { AssessmentOutlined } from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import VideoSettingsOutlinedIcon from '@mui/icons-material/VideoSettingsOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import MicIcon from '@mui/icons-material/Mic';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutConfirmDialog from '../../admin/component/LogoutConfirmDialog';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

const drawerWidthOpen = 280;
const drawerWidthClosed = 72;

const NAV_ICON_PALETTES = {
  settings: { bg: 'linear-gradient(135deg, rgba(14,165,233,0.2) 0%, rgba(217,70,239,0.2) 100%)', color: '#0ea5e9', border: 'rgba(14, 165, 233, 0.35)' },
  notifications: { bg: 'rgba(251, 191, 36, 0.22)', color: '#b45309', border: 'rgba(251, 191, 36, 0.4)' },
  email: { bg: 'rgba(59, 130, 246, 0.2)', color: '#1d4ed8', border: 'rgba(59, 130, 246, 0.35)' },
  whatsapp: { bg: 'rgba(34, 197, 94, 0.2)', color: '#15803d', border: 'rgba(34, 197, 94, 0.4)' },
  administration: { bg: 'rgba(99, 102, 241, 0.2)', color: '#4338ca', border: 'rgba(99, 102, 241, 0.35)' },
  users: { bg: 'rgba(14, 165, 233, 0.2)', color: '#0369a1', border: 'rgba(14, 165, 233, 0.35)' },
  credits: { bg: 'rgba(16, 185, 129, 0.2)', color: '#047857', border: 'rgba(16, 185, 129, 0.4)' },
  configuration: { bg: 'rgba(139, 92, 246, 0.2)', color: '#6d28d9', border: 'rgba(139, 92, 246, 0.35)' },
  interview: { bg: 'rgba(6, 182, 212, 0.2)', color: '#0e7490', border: 'rgba(6, 182, 212, 0.4)' },
  appStatus: { bg: 'rgba(244, 63, 94, 0.18)', color: '#be123c', border: 'rgba(244, 63, 94, 0.35)' },
  branding: { bg: 'rgba(236, 72, 153, 0.2)', color: '#be185d', border: 'rgba(236, 72, 153, 0.4)' },
  logo: { bg: 'rgba(168, 85, 247, 0.2)', color: '#7e22ce', border: 'rgba(168, 85, 247, 0.4)' },
  localization: { bg: 'rgba(20, 184, 166, 0.2)', color: '#0f766e', border: 'rgba(20, 184, 166, 0.4)' },
  languages: { bg: 'rgba(56, 189, 248, 0.2)', color: '#0369a1', border: 'rgba(56, 189, 248, 0.4)' },
  version: { bg: 'rgba(100, 116, 139, 0.18)', color: '#334155', border: 'rgba(100, 116, 139, 0.35)' },
  dashboard: { bg: 'rgba(14, 165, 233, 0.2)', color: '#0284c7', border: 'rgba(14, 165, 233, 0.35)' },
  talent: { bg: 'rgba(139, 92, 246, 0.2)', color: '#6d28d9', border: 'rgba(139, 92, 246, 0.35)' },
  candidates: { bg: 'rgba(59, 130, 246, 0.2)', color: '#1d4ed8', border: 'rgba(59, 130, 246, 0.35)' },
  interviews: { bg: 'rgba(34, 197, 94, 0.2)', color: '#15803d', border: 'rgba(34, 197, 94, 0.35)' },
  recruitment: { bg: 'rgba(249, 115, 22, 0.2)', color: '#c2410c', border: 'rgba(249, 115, 22, 0.4)' },
  jobs: { bg: 'rgba(234, 88, 12, 0.18)', color: '#c2410c', border: 'rgba(234, 88, 12, 0.35)' },
  questionBank: { bg: 'rgba(99, 102, 241, 0.2)', color: '#4338ca', border: 'rgba(99, 102, 241, 0.35)' },
  applications: { bg: 'rgba(168, 85, 247, 0.18)', color: '#7e22ce', border: 'rgba(168, 85, 247, 0.35)' },
  bulkSchedule: { bg: 'rgba(6, 182, 212, 0.2)', color: '#0e7490', border: 'rgba(6, 182, 212, 0.4)' },
  company: { bg: 'rgba(99, 102, 241, 0.2)', color: '#4338ca', border: 'rgba(99, 102, 241, 0.35)' },
  profile: { bg: 'rgba(100, 116, 139, 0.18)', color: '#475569', border: 'rgba(100, 116, 139, 0.35)' },
  report: { bg: 'rgba(245, 158, 11, 0.2)', color: '#b45309', border: 'rgba(245, 158, 11, 0.4)' },
  feedbackInsights: { bg: 'rgba(14, 165, 233, 0.2)', color: '#0369a1', border: 'rgba(14, 165, 233, 0.35)' },
  practiceInterview: { bg: 'rgba(217, 70, 239, 0.2)', color: '#a21caf', border: 'rgba(217, 70, 239, 0.4)' },
  progress: { bg: 'rgba(99, 102, 241, 0.2)', color: '#4338ca', border: 'rgba(99, 102, 241, 0.35)' },
  resumeAnalyze: { bg: 'rgba(6, 182, 212, 0.2)', color: '#0e7490', border: 'rgba(6, 182, 212, 0.4)' },
  resumeBuilder: { bg: 'rgba(99, 102, 241, 0.2)', color: '#4338ca', border: 'rgba(99, 102, 241, 0.35)' },
};

function createNavIcon(IconComponent, palette, size = 'md') {
  const scheme = NAV_ICON_PALETTES[palette] || NAV_ICON_PALETTES.settings;
  const dimension = size === 'sm' ? 26 : 32;
  const fontSize = size === 'sm' ? 15 : 18;

  function NavIcon({ active = false, onGradient = false }) {
    const activeStyles = onGradient
      ? {
          background: 'rgba(255, 255, 255, 0.22)',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.45)',
          boxShadow: 'none',
        }
      : {
          background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
          color: '#ffffff',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          boxShadow: '0 2px 6px rgba(14, 165, 233, 0.3)',
        };

    return (
      <Box
        sx={{
          width: dimension,
          height: dimension,
          borderRadius: size === 'sm' ? '8px' : '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...(active
            ? activeStyles
            : {
                background: scheme.bg,
                color: scheme.color,
                border: `1px solid ${scheme.border}`,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
              }),
        }}
      >
        <IconComponent sx={{ fontSize, color: 'inherit' }} />
      </Box>
    );
  }

  return NavIcon;
}

function renderNavIcon(NavIcon, active = false, onGradient = false) {
  if (!NavIcon) return null;
  return <NavIcon active={active} onGradient={onGradient} />;
}

const COMPANY_ADMIN_TAB_GROUPS = {
  users: 'administration',
  credits: 'administration',
  interviewConfiguration: 'configuration',
  appStatus: 'configuration',
  languages: 'localization',
  companyLogo: 'branding',
};

function getCompanyAdminTab(search) {
  return new URLSearchParams(search).get('tab') || 'users';
}

function pathMatches(pathname, path) {
  if (!pathname || !path) return false;
  if (pathname === path) return true;
  if (path === '/candidates' && pathname.startsWith('/candidatedetails/')) return true;
  return pathname.startsWith(`${path}/`);
}

function flattenNavLinks(items = []) {
  const links = [];
  items.forEach((item) => {
    if (item.isDropdown && Array.isArray(item.subItems)) {
      links.push(...flattenNavLinks(item.subItems));
    } else if (item.path) {
      links.push(item);
    }
  });
  return links;
}

function isNavItemActive(item, location) {
  if (item.companyAdminTab) {
    if (location.pathname !== '/companyadmins') {
      return false;
    }
    const tab = getCompanyAdminTab(location.search);
    return tab === item.companyAdminTab;
  }
  if (item.path) {
    return pathMatches(location.pathname, item.path);
  }
  return false;
}

function isSettingsGroupActive(groupItem, location) {
  if (!groupItem?.subItems) {
    return false;
  }
  return groupItem.subItems.some((child) => isNavItemActive(child, location));
}

function isAnySettingsDescendantActive(items, location) {
  for (const item of items) {
    if (item.isDropdown && item.subItems) {
      if (isAnySettingsDescendantActive(item.subItems, location)) {
        return true;
      }
    } else if (isNavItemActive(item, location)) {
      return true;
    }
  }
  return false;
}

export default function ResponsiveSidebar({ open, onToggle, mobileOpen, onMobileToggle, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [userDetails] = useState(JSON.parse(localStorage.getItem('user')));
  const [openDropdowns, setOpenDropdowns] = useState({
    talent: true,
    candidates: true,
    recruitment: true,
    settings: false,
    notifications: false,
    administration: false,
    configuration: false,
    localization: false,
    branding: false,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);

  const defaultLanguage = userDetails?.default_language || 'en';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const availableLanguages = (userDetails?.available_languages && userDetails.available_languages.length > 1)
    ? userDetails.available_languages
    : ['en', 'hi', 'ar', 'fr', 'es', 'zh', 'ja', 'ko', 'bn', 'pt', 'mr'];

  const uiLanguageOptions = useMemo(
    () => [
      { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/gb.png' },
      { code: 'hi', name: 'हिंदी', flag: 'https://flagcdn.com/w40/in.png' },
      { code: 'bn', name: 'বাংলা', flag: 'https://flagcdn.com/w40/bd.png' },
      { code: 'mr', name: 'मराठी', flag: 'https://flagcdn.com/w40/in.png' },
      { code: 'ar', name: 'العربية', flag: 'https://flagcdn.com/w40/sa.png' },
      { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w40/es.png' },
      { code: 'fr', name: 'Français', flag: 'https://flagcdn.com/w40/fr.png' },
      { code: 'pt', name: 'Português', flag: 'https://flagcdn.com/w40/br.png' },
      { code: 'zh', name: '中文', flag: 'https://flagcdn.com/w40/cn.png' },
      { code: 'ja', name: '日本語', flag: 'https://flagcdn.com/w40/jp.png' },
      { code: 'ko', name: '한국어', flag: 'https://flagcdn.com/w40/kr.png' },
    ].filter((lang) => availableLanguages.includes(lang.code)),
    [availableLanguages]
  );

  const userLanguagesForModal = useMemo(
    () =>
      uiLanguageOptions.map((lang) => ({
        code: lang.code,
        name: lang.name,
        flag: (
          <img
            src={lang.flag}
            width="24"
            height="24"
            style={{ borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' }}
            alt={lang.name}
          />
        ),
      })),
    [uiLanguageOptions]
  );

  // Set default language from token on mount and ensure RTL is set correctly
  useEffect(() => {
    const currentLang = i18n.language || defaultLanguage;

    if (defaultLanguage && i18n.language !== defaultLanguage) {
      const storedLang = localStorage.getItem('i18nextLng');
      // Only set default if stored language is not in available languages
      if (!storedLang || !availableLanguages.includes(storedLang)) {
        i18n.changeLanguage(defaultLanguage);
        // Set RTL direction based on the language being set
        setDocumentDirection(defaultLanguage);
      } else {
        // Use stored language and set RTL direction
        setDocumentDirection(storedLang);
      }
    } else {
      // Set RTL direction based on current language
      setDocumentDirection(currentLang);
    }
  }, [availableLanguages, defaultLanguage, i18n]);

  // Auto-expand menus based on current path
  useEffect(() => {
    const path = location.pathname;
    const updates = {};

    if (
      path === '/candidates'
      || path.startsWith('/candidates/')
      || path.startsWith('/candidatedetails/')
      || path.startsWith('/candidate-groups')
      || path.includes('/interview-overview')
    ) {
      updates.talent = true;
      updates.candidates = true;
    }
    if (path.includes('/jobdescriptions') || path.includes('/job-applications') || path.includes('/bulk-schedule')) {
      updates.recruitment = true;
    }
    if (path.includes('/settings/email') || path.includes('/settings/whatsapp')) {
      updates.settings = true;
      updates.notifications = true;
    }
    if (path === '/companyadmins') {
      const tab = getCompanyAdminTab(location.search);
      const group = COMPANY_ADMIN_TAB_GROUPS[tab] || 'administration';
      updates.settings = true;
      updates[group] = true;
    }
    if (path.includes('/settings/version-details')) {
      updates.settings = true;
    }

    if (Object.keys(updates).length > 0) {
      setOpenDropdowns((prev) => ({ ...prev, ...updates }));
    }
  }, [location.pathname, location.search]);

  const isActive = (paths) => {
    const pathList = Array.isArray(paths) ? paths : [paths];
    return pathList.filter(Boolean).some((path) => pathMatches(location.pathname, path));
  };

  const handleNavigation = (path) => {
    if (window.hasUnsavedQuestionnaireChanges) {
      if (!window.confirm("You have unsaved questionnaire changes. Are you sure you want to leave?")) {
        return;
      }
      window.hasUnsavedQuestionnaireChanges = false;
    }
    navigate(path);
    if (isMobile) {
      onMobileToggle();
    }
  };

  const handleSettingsItemClick = (item) => {
    if (window.hasUnsavedQuestionnaireChanges) {
      if (!window.confirm("You have unsaved questionnaire changes. Are you sure you want to leave?")) {
        return;
      }
      window.hasUnsavedQuestionnaireChanges = false;
    }
    if (item.onClick) {
      item.onClick();
    } else if (item.companyAdminTab) {
      const params = new URLSearchParams({ tab: item.companyAdminTab });
      navigate(`/companyadmins?${params.toString()}`);
    } else if (item.path) {
      navigate(item.path);
    }
    if (isMobile) {
      onMobileToggle();
    }
  };

  const getDropdownOpen = (dropdownKey) => openDropdowns[dropdownKey] ?? false;

  const toggleDropdown = (dropdownKey) => {
    setOpenDropdowns((prev) => ({ ...prev, [dropdownKey]: !prev[dropdownKey] }));
  };

  const closeSettingsDropdowns = () => {
    setOpenDropdowns((prev) => ({
      ...prev,
      settings: false,
      notifications: false,
      administration: false,
      configuration: false,
      localization: false,
      branding: false,
    }));
  };

  // Keep Settings collapsed when the sidebar is in icon-only mode
  useEffect(() => {
    if (!open && !isMobile) {
      closeSettingsDropdowns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isMobile]);

  const handleSettingsCollapsedClick = () => {
    setOpenDropdowns((prev) => ({ ...prev, settings: true }));
    onToggle?.();
  };

  // Define navigation items based on user role
  const getNavItems = () => {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const role = localStorage.getItem('role') || 'Candidate';

    const baseItems = [
      {
        label: t('nav.dashboard'),
        path: '/dashboard',
        icon: createNavIcon(DashboardIcon, 'dashboard'),
      }
    ];

    const isSuperAdmin = userDetails?.is_superuser === true;
    const isCompanyAdmin = Array.isArray(userDetails?.admin_companies) && userDetails.admin_companies.length > 0;
    const isB2CUser = role === 'Candidate';

    let mainItems = [];

    if (isSuperAdmin) {
      mainItems = [
        ...baseItems,
        {
          label: t('nav.talentManagement'),
          isDropdown: true,
          dropdownKey: 'talent',
          icon: createNavIcon(GroupsIcon, 'talent'),
          defaultOpen: true,
            subItems: [
            {
              label: t('nav.candidates'),
              isDropdown: true,
              dropdownKey: 'candidates',
              icon: createNavIcon(PeopleIcon, 'candidates', 'sm'),
              subItems: [
                {
                  label: t('nav.allCandidates', 'All Candidates'),
                  path: '/candidates',
                  icon: createNavIcon(PeopleIcon, 'allCandidates', 'sm'),
                },
                {
                  label: t('nav.candidateGroups', 'Candidate Groups'),
                  path: '/candidate-groups',
                  icon: createNavIcon(FolderSharedIcon, 'candidateGroups', 'sm'),
                },
              ],
            }
          ]
        },
        {
          label: t('nav.recruitment'),
          isDropdown: true,
          dropdownKey: 'recruitment',
          icon: createNavIcon(WorkOutlineIcon, 'recruitment'),
          defaultOpen: true,
          subItems: [
            {
              label: t('nav.jobDescriptions'),
              path: '/jobdescriptions',
              icon: createNavIcon(WorkIcon, 'jobs', 'sm'),
            },
            {
              label: t('nav.questionBank', 'Question Bank'),
              path: '/question-bank',
              icon: createNavIcon(QuizOutlinedIcon, 'questionBank', 'sm'),
            }
          ]
        },
        {
          label: t('nav.company'),
          path: '/company',
          icon: createNavIcon(BusinessIcon, 'company'),
        }
      ];
    } else if (isCompanyAdmin) {
      mainItems = [
        ...baseItems,
        {
          label: t('nav.talentManagement'),
          isDropdown: true,
          dropdownKey: 'talent',
          icon: createNavIcon(GroupsIcon, 'talent'),
          defaultOpen: true,
          subItems: [
            {
              label: t('nav.candidates'),
              isDropdown: true,
              dropdownKey: 'candidates',
              icon: createNavIcon(PeopleIcon, 'candidates', 'sm'),
              subItems: [
                {
                  label: t('nav.allCandidates', 'All Candidates'),
                  path: '/candidates',
                  icon: createNavIcon(PeopleIcon, 'allCandidates', 'sm'),
                },
                {
                  label: t('nav.candidateGroups', 'Candidate Groups'),
                  path: '/candidate-groups',
                  icon: createNavIcon(FolderSharedIcon, 'candidateGroups', 'sm'),
                },
              ],
            },
            {
              label: t('nav.interviewOverview'),
              path: '/interview-overview',
              icon: createNavIcon(AssignmentTurnedInIcon, 'interviews', 'sm'),
            }
          ]
        },
        {
          label: t('nav.recruitment'),
          isDropdown: true,
          dropdownKey: 'recruitment',
          icon: createNavIcon(WorkOutlineIcon, 'recruitment'),
          defaultOpen: true,
          subItems: [
            {
              label: t('nav.jobDescriptions'),
              path: '/jobdescriptions',
              icon: createNavIcon(WorkIcon, 'jobs', 'sm'),
            },
            {
              label: t('nav.questionBank', 'Question Bank'),
              path: '/question-bank',
              icon: createNavIcon(QuizOutlinedIcon, 'questionBank', 'sm'),
            },
            {
              label: t('nav.jobApplications'),
              path: '/job-applications',
              icon: createNavIcon(AssignmentIcon, 'applications', 'sm'),
            },
            {
              label: t('nav.bulkSchedule'),
              path: '/bulk-schedule',
              icon: createNavIcon(EventRepeatIcon, 'bulkSchedule', 'sm'),
            }
          ]
        }
      ];
    } else {
      mainItems = [
        ...baseItems,
        {
          label: t('nav.details'),
          path: '/details',
          icon: createNavIcon(ManageAccountsIcon, 'profile'),
        },
        {
          label: t('nav.recentJobs'),
          path: '/publicjobdescriptions',
          icon: createNavIcon(WorkIcon, 'jobs'),
        },
        {
          label: t('nav.practiceInterview', 'Practice Interview'),
          path: '/practice-interview',
          icon: createNavIcon(MicIcon, 'practiceInterview'),
        },
        {
          label: t('nav.myProgress', 'My Progress'),
          path: '/progress',
          icon: createNavIcon(TrendingUpIcon, 'progress'),
        },
        {
          label: t('nav.myApplications'),
          path: '/my-applications',
          icon: createNavIcon(AssignmentIcon, 'applications'),
        },
        {
          label: t('nav.resumeBuilder', 'Resume Builder'),
          path: '/resume-builder',
          icon: createNavIcon(DescriptionIcon, 'resumeBuilder'),
          badge: 'NEW',
        },
        {
          label: t('nav.resumeAnalyze', 'Resume Analyze'),
          path: '/resume-analyze',
          icon: createNavIcon(DescriptionOutlinedIcon, 'resumeAnalyze'),
        }
      ];
    }

    // Add profile section items at the bottom
    const profileItems = [];

    if (role !== 'Candidate') {
      profileItems.push({
        label: t('nav.report'),
        path: '/report',
        icon: createNavIcon(AssessmentOutlined, 'report'),
        isProfileSection: true
      });
    }

    // if (isCompanyAdmin && !isSuperAdmin) {
    //   profileItems.push({
    //     label: t('nav.feedbackInsights', 'Feedback Insights'),
    //     path: '/feedback-insights',
    //     icon: createNavIcon(InsightsOutlinedIcon, 'feedbackInsights'),
    //     isProfileSection: true
    //   });
    // }

    // Extract Settings section to render separately before logout
    let settingsItem = null;
    const settingsSubItems = [];
    const canAccessCompanyAdmin = isSuperAdmin || isCompanyAdmin;

    // Notifications (Email + WhatsApp) - company admins and super admins
    if (canAccessCompanyAdmin) {
      settingsSubItems.push({
        label: t('nav.notifications', 'Notifications'),
        isDropdown: true,
        dropdownKey: 'notifications',
        icon: createNavIcon(NotificationsActiveIcon, 'notifications'),
        subItems: [
          {
            label: t('nav.email', 'Email'),
            path: '/settings/email',
            icon: createNavIcon(MarkEmailUnreadIcon, 'email', 'sm'),
          },
          {
            label: t('nav.whatsapp', 'WhatsApp'),
            path: '/settings/whatsapp',
            icon: createNavIcon(WhatsAppIcon, 'whatsapp', 'sm'),
          },
        ],
      });
    }

    if (canAccessCompanyAdmin) {
      const administrationItems = [
        {
          label: t('companyAdmins.tabUserManagement'),
          path: '/companyadmins',
          companyAdminTab: 'users',
          icon: createNavIcon(SupervisorAccountOutlinedIcon, 'users', 'sm'),
        },
        {
          label: t('companyAdmins.creditsTab.tabTitle', 'Credits'),
          path: '/companyadmins',
          companyAdminTab: 'credits',
          icon: createNavIcon(SavingsOutlinedIcon, 'credits', 'sm'),
        },
      ];

      settingsSubItems.push({
        label: t('nav.administration', 'Administration'),
        isDropdown: true,
        dropdownKey: 'administration',
        icon: createNavIcon(AdminPanelSettingsIcon, 'administration'),
        subItems: administrationItems,
      });

      settingsSubItems.push({
        label: t('nav.configuration', 'Configuration'),
        isDropdown: true,
        dropdownKey: 'configuration',
        icon: createNavIcon(BuildCircleOutlinedIcon, 'configuration'),
        subItems: [
          {
            label: t('companyAdmins.interviewConfig.title', 'Interview Configuration'),
            path: '/companyadmins',
            companyAdminTab: 'interviewConfiguration',
            icon: createNavIcon(VideoSettingsOutlinedIcon, 'interview', 'sm'),
          },
          {
            label: t('companyAdmins.tabAppStatus'),
            path: '/companyadmins',
            companyAdminTab: 'appStatus',
            icon: createNavIcon(HealthAndSafetyOutlinedIcon, 'appStatus', 'sm'),
          },
          {
            label: t('nav.questionnaire', 'Questionnaire'),
            path: '/settings/questionnaire',
            icon: createNavIcon(DescriptionOutlinedIcon, 'questionnaire', 'sm'),
          },
        ],
      });

      settingsSubItems.push({
        label: t('nav.branding', 'Branding'),
        isDropdown: true,
        dropdownKey: 'branding',
        icon: createNavIcon(BrushOutlinedIcon, 'branding'),
        subItems: [
          {
            label: t('companyAdmins.tabCompanyLogo'),
            path: '/companyadmins',
            companyAdminTab: 'companyLogo',
            icon: createNavIcon(BrandingWatermarkOutlinedIcon, 'logo', 'sm'),
          },
        ],
      });
    }

    if (canAccessCompanyAdmin) {
      settingsSubItems.push({
        label: t('nav.localization', 'Localization'),
        isDropdown: true,
        dropdownKey: 'localization',
        icon: createNavIcon(PublicOutlinedIcon, 'localization'),
        subItems: [
          {
            label: t('companyAdmins.tabLanguages'),
            path: '/companyadmins',
            companyAdminTab: 'languages',
            icon: createNavIcon(LanguageOutlinedIcon, 'languages', 'sm'),
          },
        ],
      });
    }

    if (isB2CUser) {
      settingsSubItems.push({
        label: t('nav.localization', 'Localization'),
        isDropdown: true,
        dropdownKey: 'localization',
        icon: createNavIcon(PublicOutlinedIcon, 'localization'),
        subItems: [
          {
            label: t('companyAdmins.tabLanguages'),
            onClick: () => setLangModalOpen(true),
            icon: createNavIcon(LanguageOutlinedIcon, 'languages', 'sm'),
          },
        ],
      });
    }

    // Version Details - hidden for B2C, visible for B2B
    if (!isB2CUser) {
      settingsSubItems.push({
        label: t('nav.versionDetails'),
        path: '/settings/version-details',
        icon: createNavIcon(NewReleasesOutlinedIcon, 'version'),
      });
    }

    // Only create settings item if there are sub-items
    if (settingsSubItems.length > 0) {
      settingsItem = {
        label: t('nav.settings'),
        isDropdown: true,
        dropdownKey: 'settings',
        icon: createNavIcon(SettingsIcon, 'settings'),
        defaultOpen: false,
        subItems: settingsSubItems
      };
    }

    return { mainItems: [...mainItems, ...profileItems], settingsItem };
  };

  const { mainItems: navItems, settingsItem } = getNavItems();

  // Make isRTL reactive to direction changes
  const [isRTLState, setIsRTLState] = useState(() => getIsRTL());

  useEffect(() => {
    const checkDirection = () => {
      setIsRTLState(getIsRTL());
    };

    // Check immediately
    checkDirection();

    // Create a MutationObserver to watch for dir attribute changes
    const observer = new MutationObserver(checkDirection);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir']
    });

    return () => observer.disconnect();
  }, []);

  const drawerContent = (
    <>
      {/* Mobile drawer header — desktop collapse uses SidebarEdgeRail */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderBottom: '1px solid rgba(229, 229, 229, 0.3)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '800',
                fontSize: '1rem',
              }}
            >
              A
            </Box>
          </Box>
          <IconButton
            onClick={onMobileToggle}
            sx={{
              color: '#525252',
              '&:hover': {
                background: 'rgba(14, 165, 233, 0.1)',
                color: '#0ea5e9',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <List sx={{ px: 1, py: 2 }}>
        {navItems.map((item, index) => (
          <React.Fragment key={item.dropdownKey || item.path || item.label}>
            {/* Show divider only if there are profile items and this is the first profile item */}
            {/* {item.isProfileSection && index > 0 && navItems[index - 1] && !navItems[index - 1].isProfileSection && (
              <Divider sx={{ my: 1.5, borderColor: 'rgba(229, 229, 229, 0.5)' }} />
            )} */}

            {item.isDropdown ? (
              <>
                {/* Parent Menu Item - Only show when expanded or mobile */}
                {(open || isMobile) && (
                  <Tooltip title={''} placement="right">
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => {
                          toggleDropdown(item.dropdownKey);
                        }}
                        sx={{
                          borderRadius: '12px',
                          minHeight: 48,
                          justifyContent: 'initial',
                          px: 2.5,
                          background: isActive(flattenNavLinks(item.subItems).map((sub) => sub.path))
                            ? 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
                            : 'transparent',
                          color: isActive(flattenNavLinks(item.subItems).map((sub) => sub.path)) ? 'white' : '#525252',
                          '&:hover': {
                            background: isActive(flattenNavLinks(item.subItems).map((sub) => sub.path))
                              ? 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)'
                              : 'rgba(14, 165, 233, 0.1)',
                            color: isActive(flattenNavLinks(item.subItems).map((sub) => sub.path)) ? 'white' : '#0ea5e9',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 2,
                            justifyContent: 'center',
                            color: 'inherit',
                          }}
                        >
                          {renderNavIcon(
                            item.icon,
                            isActive(flattenNavLinks(item.subItems).map((sub) => sub.path)),
                            true
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            '& .MuiTypography-root': {
                              fontWeight: 600,
                              fontSize: '0.9rem',
                            }
                          }}
                        />
                        {getDropdownOpen(item.dropdownKey) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                )}

                {/* Sub Items */}
                {(open || isMobile) ? (
                  // When expanded - show as nested items
                  <Collapse
                    in={getDropdownOpen(item.dropdownKey)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {item.subItems.map((subItem) => {
                        if (subItem.isDropdown && subItem.subItems) {
                          const childPaths = flattenNavLinks(subItem.subItems).map((child) => child.path);
                          const groupActive = isActive(childPaths);
                          return (
                            <React.Fragment key={subItem.dropdownKey || subItem.label}>
                              <ListItem disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                  onClick={() => toggleDropdown(subItem.dropdownKey)}
                                  sx={{
                                    borderRadius: '12px',
                                    minHeight: 40,
                                    pl: 6,
                                    background: groupActive ? 'rgba(14, 165, 233, 0.08)' : 'transparent',
                                    color: groupActive ? '#0ea5e9' : '#737373',
                                    '&:hover': {
                                      background: 'rgba(14, 165, 233, 0.1)',
                                      color: '#0ea5e9',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 0, mr: 2, color: 'inherit' }}>
                                    {renderNavIcon(subItem.icon, groupActive)}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={subItem.label}
                                    sx={{
                                      '& .MuiTypography-root': {
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        fontFamily: 'Poppins, sans-serif',
                                      },
                                    }}
                                  />
                                  {getDropdownOpen(subItem.dropdownKey) ? (
                                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                                  )}
                                </ListItemButton>
                              </ListItem>
                              <Collapse in={getDropdownOpen(subItem.dropdownKey)} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {subItem.subItems.map((child) => (
                                    <ListItem key={child.path || child.label} disablePadding sx={{ mb: 0.5 }}>
                                      <ListItemButton
                                        onClick={() => handleNavigation(child.path)}
                                        sx={{
                                          borderRadius: '12px',
                                          minHeight: 36,
                                          pl: 8,
                                          background: isActive(child.path) ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
                                          color: isActive(child.path) ? '#0ea5e9' : '#737373',
                                          '&:hover': {
                                            background: 'rgba(14, 165, 233, 0.1)',
                                            color: '#0ea5e9',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 0, mr: 2, color: 'inherit' }}>
                                          {renderNavIcon(child.icon, isActive(child.path))}
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={child.label}
                                          sx={{
                                            '& .MuiTypography-root': {
                                              fontWeight: 500,
                                              fontSize: '0.8rem',
                                              fontFamily: 'Poppins, sans-serif',
                                            },
                                          }}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        }
                        return (
                        <ListItem key={subItem.label} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => handleNavigation(subItem.path)}
                            sx={{
                              borderRadius: '12px',
                              minHeight: 40,
                              pl: 6,
                              background: isActive(subItem.path)
                                ? 'rgba(14, 165, 233, 0.15)'
                                : 'transparent',
                              color: isActive(subItem.path) ? '#0ea5e9' : '#737373',
                              '&:hover': {
                                background: 'rgba(14, 165, 233, 0.1)',
                                color: '#0ea5e9',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 0,
                                mr: 2,
                                color: 'inherit',
                              }}
                            >
                              {renderNavIcon(subItem.icon, isActive(subItem.path))}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.label}
                              sx={{
                                '& .MuiTypography-root': {
                                  fontWeight: 500,
                                  fontSize: '0.85rem',
                                  fontFamily: 'Poppins, sans-serif',
                                }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                ) : (
                  // When collapsed - show sub-items as direct icons
                  flattenNavLinks(item.subItems).map((subItem) => (
                    <Tooltip key={subItem.label} title={subItem.label} placement="right">
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => handleNavigation(subItem.path)}
                          sx={{
                            borderRadius: '12px',
                            minHeight: 48,
                            justifyContent: 'center',
                            px: 2.5,
                            background: isActive(subItem.path)
                              ? 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
                              : 'transparent',
                            color: isActive(subItem.path) ? 'white' : '#525252',
                            '&:hover': {
                              background: isActive(subItem.path)
                                ? 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)'
                                : 'rgba(14, 165, 233, 0.1)',
                              color: isActive(subItem.path) ? 'white' : '#0ea5e9',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 0,
                              mr: 'auto',
                              justifyContent: 'center',
                              color: 'inherit',
                            }}
                          >
                            {renderNavIcon(subItem.icon, isActive(subItem.path), true)}
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  ))
                )}
              </>
            ) : (
              <Tooltip title={(!isMobile && !open) ? item.label : ''} placement="right">
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      borderRadius: '12px',
                      minHeight: 48,
                      justifyContent: (open || isMobile) ? 'initial' : 'center',
                      px: 2.5,
                      background: isActive(item.path)
                        ? 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
                        : 'transparent',
                      color: isActive(item.path) ? 'white' : (item.isProfileSection ? '#525252' : '#525252'),
                      '&:hover': {
                        background: isActive(item.path)
                          ? 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)'
                          : 'rgba(14, 165, 233, 0.1)',
                        color: isActive(item.path) ? 'white' : '#0ea5e9',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: (open || isMobile) ? 2 : 'auto',
                        justifyContent: 'center',
                        color: 'inherit',
                      }}
                    >
                      {renderNavIcon(item.icon, isActive(item.path), true)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        opacity: (open || isMobile) ? 1 : 0,
                        fontFamily: 'Poppins, sans-serif',
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          fontSize: '0.9rem',
                        }
                      }}
                    />
                    {item.badge && (open || isMobile) && (
                      <Box
                        sx={{
                          background: 'rgba(34, 197, 94, 0.15)',
                          color: '#15803d',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          px: 1,
                          py: 0.2,
                          borderRadius: '4px',
                          ml: 1,
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      >
                        {item.badge}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            )}
          </React.Fragment>
        ))}


        {/* Settings Section - Rendered before logout */}
        {settingsItem && (
          <>
            {settingsItem.isDropdown ? (
              <>
                {/* Parent Menu Item - Only show when expanded or mobile */}
                {(open || isMobile) && (
                  <Tooltip title={''} placement="right">
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => {
                          toggleDropdown(settingsItem.dropdownKey);
                        }}
                        sx={{
                          borderRadius: '12px',
                          minHeight: 48,
                          justifyContent: 'initial',
                          px: 2.5,
                          background: isAnySettingsDescendantActive(settingsItem.subItems, location)
                            ? 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
                            : 'transparent',
                          color: isAnySettingsDescendantActive(settingsItem.subItems, location) ? 'white' : '#525252',
                          '&:hover': {
                            background: isAnySettingsDescendantActive(settingsItem.subItems, location)
                              ? 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)'
                              : 'rgba(14, 165, 233, 0.1)',
                            color: isAnySettingsDescendantActive(settingsItem.subItems, location) ? 'white' : '#0ea5e9',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            mr: 1.5,
                            justifyContent: 'center',
                            color: 'inherit',
                          }}
                        >
                          {renderNavIcon(
                            settingsItem.icon,
                            isAnySettingsDescendantActive(settingsItem.subItems, location),
                            true
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={settingsItem.label}
                          sx={{
                            fontFamily: 'Poppins, sans-serif',
                            '& .MuiTypography-root': {
                              fontWeight: 600,
                              fontSize: '0.9rem',
                            }
                          }}
                        />
                        {getDropdownOpen(settingsItem.dropdownKey) ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                )}

                {/* Sub Items */}
                {(open || isMobile) ? (
                  <Collapse
                    in={getDropdownOpen(settingsItem.dropdownKey)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {settingsItem.subItems.map((subItem) => {
                        // Nested dropdown (Notifications, Administration, etc.)
                        if (subItem.isDropdown && subItem.subItems) {
                          const groupActive = isSettingsGroupActive(subItem, location);
                          return (
                            <React.Fragment key={subItem.dropdownKey || subItem.label}>
                              <ListItem disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                  onClick={() => toggleDropdown(subItem.dropdownKey)}
                                  sx={{
                                    borderRadius: '12px',
                                    minHeight: 40,
                                    pl: 6,
                                    background: groupActive
                                      ? 'rgba(14, 165, 233, 0.08)'
                                      : 'transparent',
                                    color: groupActive ? '#0ea5e9' : '#737373',
                                    '&:hover': {
                                      background: 'rgba(14, 165, 233, 0.1)',
                                      color: '#0ea5e9',
                                    },
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36, mr: 1.5, color: 'inherit' }}>
                                    {renderNavIcon(subItem.icon, groupActive)}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={subItem.label}
                                    sx={{
                                      '& .MuiTypography-root': {
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                        fontFamily: 'Poppins, sans-serif',
                                      },
                                    }}
                                  />
                                  {getDropdownOpen(subItem.dropdownKey) ? (
                                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                                  )}
                                </ListItemButton>
                              </ListItem>
                              <Collapse
                                in={getDropdownOpen(subItem.dropdownKey)}
                                timeout="auto"
                                unmountOnExit
                              >
                                <List component="div" disablePadding>
                                  {subItem.subItems.map((child) => (
                                    <ListItem key={child.path || child.label} disablePadding sx={{ mb: 0.5 }}>
                                      <ListItemButton
                                        onClick={() => handleSettingsItemClick(child)}
                                        sx={{
                                          borderRadius: '12px',
                                          minHeight: 36,
                                          pl: 8,
                                          background: isNavItemActive(child, location)
                                            ? 'rgba(14, 165, 233, 0.15)'
                                            : 'transparent',
                                          color: isNavItemActive(child, location) ? '#0ea5e9' : '#737373',
                                          '&:hover': {
                                            background: 'rgba(14, 165, 233, 0.1)',
                                            color: '#0ea5e9',
                                          },
                                          transition: 'all 0.2s ease',
                                        }}
                                      >
                                        <ListItemIcon sx={{ minWidth: 34, mr: 1.5, color: 'inherit' }}>
                                          {renderNavIcon(
                                            child.icon,
                                            isNavItemActive(child, location)
                                          )}
                                        </ListItemIcon>
                                        <ListItemText
                                          primary={child.label}
                                          sx={{
                                            '& .MuiTypography-root': {
                                              fontWeight: 500,
                                              fontSize: '0.8rem',
                                              fontFamily: 'Poppins, sans-serif',
                                            },
                                          }}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        }

                        // Regular sub-item (Version Details)
                        return (
                          <ListItem key={subItem.label} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                              onClick={() => handleSettingsItemClick(subItem)}
                              sx={{
                                borderRadius: '12px',
                                minHeight: 40,
                                pl: 6,
                                background: isNavItemActive(subItem, location)
                                  ? 'rgba(14, 165, 233, 0.15)'
                                  : 'transparent',
                                color: isNavItemActive(subItem, location) ? '#0ea5e9' : '#737373',
                                '&:hover': {
                                  background: 'rgba(14, 165, 233, 0.1)',
                                  color: '#0ea5e9',
                                },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 36,
                                  mr: 1.5,
                                  color: 'inherit',
                                }}
                              >
                                {renderNavIcon(
                                  subItem.icon,
                                  isNavItemActive(subItem, location)
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.label}
                                sx={{
                                  '& .MuiTypography-root': {
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                    fontFamily: 'Poppins, sans-serif',
                                  }
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                ) : (
                  <Tooltip title={settingsItem.label} placement="right">
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={handleSettingsCollapsedClick}
                        sx={{
                          borderRadius: '12px',
                          minHeight: 48,
                          justifyContent: 'center',
                          px: 2.5,
                          background: isAnySettingsDescendantActive(settingsItem.subItems, location)
                            ? 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
                            : 'transparent',
                          color: isAnySettingsDescendantActive(settingsItem.subItems, location)
                            ? 'white'
                            : '#525252',
                          '&:hover': {
                            background: isAnySettingsDescendantActive(settingsItem.subItems, location)
                              ? 'linear-gradient(135deg, #0284c7 0%, #c026d3 100%)'
                              : 'rgba(14, 165, 233, 0.1)',
                            color: isAnySettingsDescendantActive(settingsItem.subItems, location)
                              ? 'white'
                              : '#0ea5e9',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 'auto',
                            justifyContent: 'center',
                            color: 'inherit',
                          }}
                        >
                          {renderNavIcon(
                            settingsItem.icon,
                            isAnySettingsDescendantActive(settingsItem.subItems, location),
                            true
                          )}
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                )}
              </>
            ) : null}
          </>
        )}

        {/* Divider before logout */}
        <Divider sx={{ my: 2, borderColor: 'rgba(229, 229, 229, 0.5)' }} />

        {/* Logout Button */}
        <Tooltip title={(!isMobile && !open) ? 'Logout' : ''} placement="right">
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setDialogOpen(true)}
              sx={{
                borderRadius: '12px',
                minHeight: 48,
                justifyContent: (open || isMobile) ? 'initial' : 'center',
                px: 2.5,
                color: '#ef4444',
                '&:hover': {
                  background: 'rgba(239, 68, 68, 0.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: (open || isMobile) ? 2 : 'auto',
                  justifyContent: 'center',
                  color: 'inherit',
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('nav.logout')}
                sx={{
                  opacity: (open || isMobile) ? 1 : 0,
                  fontFamily: 'Poppins, sans-serif',
                  '& .MuiTypography-root': {
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </List>
    </>
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileToggle}
          anchor={isRTLState ? 'right' : 'left'}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidthOpen,
              boxSizing: 'border-box',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              [isRTLState ? 'borderLeft' : 'borderRight']: '1px solid rgba(229, 229, 229, 0.3)',
              mt: '64px',
              height: 'calc(100vh - 64px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <LogoutConfirmDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={() => {
            setDialogOpen(false);
            onLogout();
          }}
        />

        <LanguageSelectionModal
          open={langModalOpen}
          onClose={() => setLangModalOpen(false)}
          currentLanguage={i18n.language}
          availableLanguages={userLanguagesForModal}
          onSelectLanguage={async (code) => {
            await i18n.changeLanguage(code);
            setDocumentDirection(code);
            setTimeout(() => {
              window.dispatchEvent(new Event('languagechange'));
            }, 100);
            setLangModalOpen(false);
          }}
        />
      </>
    );
  }

  return (
    <>
      <Drawer
        variant="permanent"
        open={open}
        anchor={isRTLState ? 'right' : 'left'}
        sx={{
          width: open ? drawerWidthOpen : drawerWidthClosed,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidthOpen : drawerWidthClosed,
            boxSizing: 'border-box',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            [isRTLState ? 'borderLeft' : 'borderRight']: '1px solid rgba(229, 229, 229, 0.3)',
            boxShadow: open ? (isRTLState ? '-4px 0 12px rgba(0, 0, 0, 0.05)' : '4px 0 12px rgba(0, 0, 0, 0.05)') : 'none',
            transition: 'width 0.3s ease, box-shadow 0.3s ease',
            overflowX: 'hidden',
            mt: '64px',
            height: 'calc(100vh - 64px)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <LogoutConfirmDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={() => {
          setDialogOpen(false);
          onLogout();
        }}
      />

      <LanguageSelectionModal
        open={langModalOpen}
        onClose={() => setLangModalOpen(false)}
        currentLanguage={i18n.language}
        availableLanguages={userLanguagesForModal}
        onSelectLanguage={async (code) => {
          await i18n.changeLanguage(code);
          setDocumentDirection(code);
          setTimeout(() => {
            window.dispatchEvent(new Event('languagechange'));
          }, 100);
          setLangModalOpen(false);
        }}
      />
    </>
  );
}
